// useTrendsData.js
// Hooks para leer los docs de la feature Oportunidades desde Firestore.
// Las colecciones las escribe bot/trends_engine.py vía Admin SDK; las reglas
// (firestore.rules) exigen hasActiveAccess para lectura.
//
// IMPORTANTE: este módulo usa el cliente Firestore (no RTDB). El bot escribe
// docs en `trends_opportunities/{YYYY-MM-DD}`, `trends_games/{YYYY-MM-DD}` y
// `trends_subjects/{id}`.
import { useEffect, useState, useMemo } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { firestore as fdb } from '../firebase';

// YYYY-MM-DD en hora Chile (mismo formato que escribe el job).
function todayDateCL() {
  try {
    const fmt = new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Santiago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    });
    return fmt.format(new Date()); // "YYYY-MM-DD"
  } catch (_) {
    return new Date().toISOString().slice(0, 10);
  }
}

/**
 * Hook para Hub: lee trends_games/{today} + trends_opportunities/{today}.
 * Devuelve { games, opps, generated, loading, error }.
 */
export function useTrendsToday(dateOverride) {
  const date = dateOverride || todayDateCL();
  const [games, setGames] = useState([]);
  const [opps, setOpps] = useState([]);
  const [generated, setGenerated] = useState(null);
  const [loadingGames, setLoadingGames] = useState(true);
  const [loadingOpps, setLoadingOpps] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoadingGames(true);
    setLoadingOpps(true);

    const unsubGames = onSnapshot(
      doc(fdb, 'trends_games', date),
      (snap) => {
        const d = snap.data();
        setGames((d && d.games) || []);
        setLoadingGames(false);
      },
      (e) => {
        setError(e);
        setLoadingGames(false);
      }
    );
    const unsubOpps = onSnapshot(
      doc(fdb, 'trends_opportunities', date),
      (snap) => {
        const d = snap.data();
        setOpps((d && d.opportunities) || []);
        setGenerated((d && d.generated_at) || null);
        setLoadingOpps(false);
      },
      (e) => {
        setError(e);
        setLoadingOpps(false);
      }
    );

    return () => {
      unsubGames();
      unsubOpps();
    };
  }, [date]);

  return {
    date,
    games,
    opps,
    generated,
    loading: loadingGames || loadingOpps,
    error,
  };
}

/**
 * Hook para Detail: lee trends_subjects/{id}.
 * Devuelve { subject, loading, error }.
 * Si id es null/undefined, no se subscribe.
 */
export function useTrendSubject(id) {
  const [subject, setSubject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id) {
      setSubject(null);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const unsub = onSnapshot(
      doc(fdb, 'trends_subjects', id),
      (snap) => {
        setSubject(snap.data() || null);
        setLoading(false);
      },
      (e) => {
        setError(e);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [id]);

  return { subject, loading, error };
}

// ── Mapeo backend → schema esperado por EdgeEliteTrends ────────────
// El componente original espera { id, type, name, abbr, league, games[]: {...} }
// Backend escribe { id, kind, name, abbr, league, l10, l20, season_avg }.
// Esto adapta sin tocar la lógica visual del componente.
const ES_MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

function formatGameLabel(iso) {
  if (!iso) return '';
  // ESPN dates suelen venir "2026-06-08" o "2026-06-08T00:30Z"
  const ymd = iso.slice(0, 10);
  const parts = ymd.split('-');
  if (parts.length !== 3) return '';
  const m = parseInt(parts[1], 10);
  const d = parseInt(parts[2], 10);
  if (Number.isNaN(m) || Number.isNaN(d)) return '';
  return `${ES_MONTHS[m - 1] || '?'} ${String(d).padStart(2, '0')}`;
}

export function adaptSubjectFromBackend(backendSub) {
  if (!backendSub) return null;
  const kind = backendSub.kind || 'team';
  const rawGames = backendSub.l20 || backendSub.l10 || [];
  const h2hGames = backendSub.h2h || [];

  // Mapping diferente según kind: teams tienen H1/total/oppScore, players PTS/REB/AST/TPM
  const mapGame = (g) => ({
    label: formatGameLabel(g.date || ''),
    opp: (g.opp_abbr || '').toLowerCase(),
    ha: g.home_away === 'home' ? 'vs' : '@',
    res: g.result,
    // Team fields
    H1: g.h1,
    total: g.total,
    oppScore: g.opp_total,
    // Player fields
    PTS: g.pts,
    REB: g.reb,
    AST: g.ast,
    TPM: g.threes,
  });

  return {
    id: backendSub.id,
    type: kind,
    name: backendSub.name,
    short: backendSub.name,
    abbr: (backendSub.abbr || '').toLowerCase(),
    league: backendSub.league,
    espnId: backendSub.espn_id || null,  // para PlayerPhoto
    pos: backendSub.pos || null,
    nextOpp: (backendSub.next_opp || '').toLowerCase(),
    nextHA: backendSub.next_ha || 'vs',
    nextTime: backendSub.next_time || '',
    games: rawGames.map(mapGame),
    h2h: h2hGames.map(mapGame),
    seasonAvg: backendSub.season_avg || {},
  };
}

// Helper para construir el SUBJECTS map del Hub adaptando solo lo
// que necesita (id, abbr, league) — para que las cards del Hub puedan
// renderizar los logos sin tener que cargar el subject completo de cada uno.
export function useMemoSubjectsFromOpps(opps) {
  return useMemo(() => {
    const map = {};
    for (const o of opps || []) {
      const sid = o.subject_id;
      if (!sid || map[sid]) continue;
      map[sid] = {
        id: sid,
        type: o.subject_kind || 'team',
        name: o.subject_name,
        short: o.subject_name,
        abbr: (sid.split('_').pop() || '').toLowerCase(),
        league: o.league,
        // No tenemos games[] embebido en la opp; el Hub solo necesita la
        // sparkline a partir de avg/line/pct → suficiente con esos campos.
      };
    }
    return map;
  }, [opps]);
}
